import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, Mail, Lock, ArrowRight, Globe, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TenantLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [slug, setSlug] = useState(searchParams.get('org') || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    if (token && user) {
      navigate('/');
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!slug) {
      toast.error('Please enter your organization slug');
      return;
    }
    setLoading(true);

    try {
      const response = await axios.post(`${API}/platform/tenant-login?slug=${slug.trim().toLowerCase()}&email=${encodeURIComponent(email.trim())}&password=${encodeURIComponent(password)}`);
      
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('tenant', JSON.stringify(response.data.tenant));
      
      toast.success(`Welcome to ${response.data.tenant.name}`);
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!slug) { toast.error('Please enter your organization slug'); return; }
    if (!email) { toast.error('Please enter your email'); return; }
    setForgotLoading(true);
    setForgotMessage('');
    try {
      const res = await axios.post(`${API}/platform/tenant-forgot-password?slug=${slug.trim().toLowerCase()}&email=${encodeURIComponent(email.trim())}`);
      setForgotMessage(res.data.message);
      toast.success('Password reset initiated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally { setForgotLoading(false); }
  };

  // Google OAuth login removed (B2B model: users are pre-provisioned by tenant
  // admins, so a public "Continue with Google" CTA was misleading). The backend
  // /api/auth/google/callback endpoint remains dormant for any inflight session.

  return (
    <div className="min-h-screen flex" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Left Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="p-3 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-lg">
                <Building2 className="h-8 w-8 text-white" />
              </div>
            </div>
            <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Recruitizr
            </h1>
            <p className="text-slate-500 mt-2">Sign in to your recruitment platform</p>
          </div>

          <Card className="border-slate-200 shadow-lg" data-testid="login-card">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-bold text-slate-900">Welcome Back</CardTitle>
              <CardDescription>Sign in to your organization&apos;s dashboard</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Organization Slug
                  </Label>
                  <div className="relative mt-2">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      data-testid="tenant-slug-login"
                      type="text"
                      value={slug}
                      onChange={(e) => setSlug(e.target.value.toLowerCase())}
                      placeholder="your-company"
                      className="pl-10 border-slate-200 focus:border-blue-500"
                      required
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">The unique identifier for your organization</p>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Email Address
                  </Label>
                  <div className="relative mt-2">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      data-testid="tenant-email-login"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="pl-10 border-slate-200 focus:border-blue-500"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Password
                  </Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      data-testid="tenant-password-login"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      className="pl-10 border-slate-200 focus:border-blue-500"
                      required
                    />
                  </div>
                </div>

                {!forgotMode ? (
                  <>
                    <Button
                      data-testid="tenant-login-btn"
                      type="submit"
                      disabled={loading}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-sm"
                    >
                      {loading ? 'Signing in...' : 'Sign In'}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <button type="button" onClick={() => { setForgotMode(true); setForgotMessage(''); }} className="w-full text-center text-sm text-blue-600 hover:underline mt-2" data-testid="forgot-password-link">
                      Forgot Password?
                    </button>
                  </>
                ) : (
                  <>
                    <Button
                      data-testid="forgot-password-btn"
                      type="button"
                      disabled={forgotLoading}
                      onClick={handleForgotPassword}
                      className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold py-3 rounded-sm"
                    >
                      {forgotLoading ? 'Processing...' : 'Reset Password'}
                    </Button>
                    {forgotMessage && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                        {forgotMessage}
                      </div>
                    )}
                    <button type="button" onClick={() => { setForgotMode(false); setForgotMessage(''); }} className="w-full text-center text-sm text-slate-500 hover:underline mt-2">
                      Back to Sign In
                    </button>
                  </>
                )}
              </form>

              <p className="text-center text-xs text-slate-400 mt-6">
                Need an account? Contact your organization&apos;s administrator.
              </p>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-slate-500 mt-6">
            Platform admin? <a href="/super-admin/login" className="text-slate-900 font-semibold hover:underline">Super Admin Portal</a>
          </p>
        </div>
      </div>

      {/* Right Panel - Features */}
      <div 
        className="hidden lg:flex flex-1 items-center justify-center p-12 relative bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/18435276/pexels-photo-18435276.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)'
        }}
      >
        <div className="absolute inset-0 bg-slate-900/80" />
        <div className="relative z-10 text-white max-w-lg">
          <h2 className="text-4xl font-extrabold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            The Complete Recruitment Platform
          </h2>
          <div className="space-y-4">
            {[
              'Position & Candidate Management',
              'Interview Scheduling & Tracking',
              'Revenue & Performance Analytics',
              'HRMS: Attendance, Leaves, Onboarding',
              'Multi-Team Collaboration'
            ].map((feature, idx) => (
              <div key={`tena-${idx}`} className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-emerald-400" />
                <span className="text-lg">{feature}</span>
              </div>
            ))}
          </div>
          <div className="mt-8 p-6 bg-white/10 rounded-lg backdrop-blur-sm border border-white/20">
            <p className="text-lg italic">
              &ldquo;Built by recruiters, for recruiters — tested on real mandates daily.&rdquo;
            </p>
            <p className="text-sm text-slate-300 mt-3">— The Recruitizr Team</p>
          </div>
        </div>
      </div>
    </div>
  );
}
